// ===========================================
// BRAINHUB CORE: Workspace Switcher
// ===========================================

import { cn } from '@/lib/utils'
import { useWorkspace } from '../providers/WorkspaceProvider'
import { Badge } from '@/components/ui/badge'
import {
  SidebarTooltip,
  SidebarTooltipProvider,
} from './sidebar/SidebarTooltip'

interface WorkspaceSwitcherProps {
  variant?: 'full' | 'compact'
  className?: string
  collapsed?: boolean
}

export function WorkspaceSwitcher({
  variant = 'full',
  className,
  collapsed = false,
}: WorkspaceSwitcherProps) {
  const { workspaceActual, workspacesDisponibles, cambiarWorkspace, isLoading } = useWorkspace()

  if (isLoading) {
    return (
      <div className={cn('flex gap-2 px-4 py-2', className)}>
        <div className="h-10 w-32 bg-muted animate-pulse rounded-lg" />
      </div>
    )
  }

  // Si solo hay un workspace, no mostrar switcher
  if (workspacesDisponibles.length <= 1) {
    return null
  }

  if (variant === 'compact') {
    return (
      <SidebarTooltipProvider>
        <div className={cn('flex gap-1', collapsed && 'flex-col items-center', className)}>
          {workspacesDisponibles.map((ws) => {
            const Icon = ws.icono
            const isActive = workspaceActual.id === ws.id

            return (
              <SidebarTooltip
                key={ws.id}
                content={ws.nombre}
                side={collapsed ? 'right' : 'bottom'}
              >
                <button
                  onClick={() => cambiarWorkspace(ws.id)}
                  className={cn(
                    'p-2 rounded-lg transition-all',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-muted hover:bg-accent text-muted-foreground hover:text-accent-foreground'
                  )}
                  style={{
                    backgroundColor: isActive ? ws.color : undefined,
                  }}
                >
                  <Icon className="h-5 w-5" />
                </button>
              </SidebarTooltip>
            )
          })}
        </div>
      </SidebarTooltipProvider>
    )
  }

  return (
    <div className={cn('flex gap-2 px-4 py-2 border-b bg-muted/30', className)}>
      {workspacesDisponibles.map((ws) => {
        const Icon = ws.icono
        const isActive = workspaceActual.id === ws.id

        return (
          <button
            key={ws.id}
            onClick={() => cambiarWorkspace(ws.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-medium',
              isActive
                ? 'text-white shadow-md'
                : 'bg-background hover:bg-accent text-muted-foreground hover:text-accent-foreground border'
            )}
            style={{
              backgroundColor: isActive ? ws.color : undefined,
            }}
          >
            <Icon className="h-5 w-5" />
            <span>{ws.nombre}</span>
            {isActive && (
              <Badge
                variant="secondary"
                className="ml-1 text-xs bg-white/20 text-white border-0"
              >
                ACTIVO
              </Badge>
            )}
          </button>
        )
      })}
    </div>
  )
}
